import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSecondScreenData } from '../../../../contexts/SecondScreenContext';
import type { TabletopTelemetryView } from '../../../../types/second-screen.types';

const BREACH_FLASH_MS = 900;

// Replaces SocConsoleWidget's 32-wide list column for the war room scene.
// SocConsoleWidget itself is left untouched (plan 06) — this is a sibling
// component with a different data shape (allSystems gives a stable
// denominator from STARTEX; SocConsoleWidget only ever had a bare count).
const SystemTileGrid: React.FC = () => {
  const { state } = useSecondScreenData();
  const tabletop = state?.tabletop;
  const allSystems = tabletop?.allSystems ?? [];
  const systemStatus = tabletop?.systemStatus ?? [];

  // Latest due severity per system, from the backend's uncapped systemStatus
  // — deliberately NOT derived from `tabletop.telemetry` (capped to the last
  // 40 for wire size), or a system's only triggering alert can scroll out of
  // that cap and its tile would silently "heal" back to neutral.
  const severityBySystem = useMemo(() => {
    const map = new Map<string, TabletopTelemetryView['severity']>();
    for (const entry of systemStatus) {
      map.set(entry.systemAffected, entry.severity);
    }
    return map;
  }, [systemStatus]);

  const degradedCount = allSystems.filter((s) => {
    const severity = severityBySystem.get(s);
    return severity === 'warning' || severity === 'critical';
  }).length;

  const prevSeverityRef = useRef<Map<string, TabletopTelemetryView['severity']>>(new Map());
  const isFirstRun = useRef(true);
  const pendingTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [breached, setBreached] = useState<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      pendingTimeouts.current.forEach((id) => clearTimeout(id));
      pendingTimeouts.current.clear();
    };
  }, []);

  useEffect(() => {
    // Seed on first mount without flashing anything — a screen reload or a
    // widget added mid-run shouldn't flash every already-critical system at
    // once, same reasoning as DecisionTickerWidget's seenIds seeding.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      severityBySystem.forEach((severity, system) => prevSeverityRef.current.set(system, severity));
      return;
    }

    const newlyBreached: string[] = [];
    severityBySystem.forEach((severity, system) => {
      const prev = prevSeverityRef.current.get(system);
      if (severity === 'critical' && prev !== 'critical') newlyBreached.push(system);
      prevSeverityRef.current.set(system, severity);
    });

    if (newlyBreached.length > 0) {
      setBreached((prev) => {
        const next = new Set(prev);
        newlyBreached.forEach((s) => next.add(s));
        return next;
      });
      newlyBreached.forEach((system) => {
        const t = setTimeout(() => {
          pendingTimeouts.current.delete(t);
          setBreached((prev) => {
            const next = new Set(prev);
            next.delete(system);
            return next;
          });
        }, BREACH_FLASH_MS);
        pendingTimeouts.current.add(t);
      });
    }
    // Only re-run when a severity value actually changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.from(severityBySystem.entries()).map(([k, v]) => `${k}:${v}`).join(',')]);

  if (allSystems.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-white/30" style={{ fontSize: '16px' }}>
        No systems authored
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full gap-1 min-h-0">
      <p className="flex-shrink-0 font-mono font-bold tabular-nums leading-none" style={{ fontSize: '80px' }}>
        <span className={degradedCount > 0 ? 'text-amber-400' : 'text-white'}>{degradedCount}</span>
        <span className="text-white/40"> of {allSystems.length}</span>
      </p>
      <p className="flex-shrink-0 text-white/50 uppercase tracking-widest" style={{ fontSize: '16px' }}>
        systems degraded
      </p>
      <div className="flex-1 min-h-0 grid grid-cols-4 auto-rows-fr gap-1.5 overflow-hidden pt-1">
        {allSystems.map((system) => {
          const severity = severityBySystem.get(system);
          const tileClass =
            severity === 'critical'
              ? 'bg-red-500/25 border-red-400/60 text-red-100'
              : severity === 'warning'
              ? 'bg-amber-500/20 border-amber-400/50 text-amber-100'
              : 'bg-white/5 border-white/10 text-white/50';
          return (
            <div
              key={system}
              className={`flex items-center justify-center rounded border px-1 text-center truncate ${tileClass}`}
              style={{
                fontSize: '16px',
                animation: breached.has(system) ? 'system-breach 900ms ease-out' : undefined,
              }}
            >
              {system}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SystemTileGrid;
